import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import OlMap from 'ol/Map';
import VectorSource from 'ol/source/Vector';
import { SelectToolComponent } from './tools/select-tool.component';
import { DrawCircleToolComponent } from './tools/draw-circle-tool.component';
import { DrawPolygonToolComponent, DrawShapeMode } from './tools/draw-polygon-tool.component';
import { SearchToolComponent, CanvasSearchMode } from './tools/search-tool.component';
import { MeasureToolComponent } from './tools/measure-tool.component';
import { SwipeToolComponent } from './tools/swipe-tool.component';
import { ImportToolComponent, type ImportedFeaturesPayload } from './tools/import-tool.component';
import { ExportToolComponent } from './tools/export-tool.component';
import { BasemapToolComponent } from './tools/basemap-tool.component';
import type { Basemap, BasemapType, CanvasExportLayerDescriptor } from '../canvas-maps.models';

@Component({
  selector: 'lib-canvas-toolbar',
  standalone: true,
  imports: [
    CommonModule,
    SelectToolComponent,
    SearchToolComponent,
    DrawCircleToolComponent,
    DrawPolygonToolComponent,
    MeasureToolComponent,
    BasemapToolComponent,
    SwipeToolComponent,
    ExportToolComponent,
    ImportToolComponent,
  ],
  templateUrl: './toolbar-home.component.html',
  styleUrl: './toolbar-home.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class CanvasToolbarComponent {
  @Input({ required: true }) map!: OlMap;
  @Input({ required: true }) userDrawSource!: VectorSource;
  @Input() addPointActive = false;
  /** Highlight the select button when no drawing tool is active. */
  @Input() selectActive = false;
  /** Highlight the swipe button when swipe mode is on. */
  @Input() swipeActive = false;
  /** Highlight the ruler while measuring distance. */
  @Input() measureActive = false;
  /** Active base map key for the base-map picker. */
  @Input() activeBasemap: BasemapType = 'osm';
  /** Base-map entries shown in the picker. */
  @Input() basemaps: readonly Basemap[] = [];
  /** Visible vector layers for toolbar export (drawings + plotted datasets). */
  @Input() exportLayers: () => readonly CanvasExportLayerDescriptor[] = () => [];

  @Output() selectTool = new EventEmitter<void>();
  @Output() locate = new EventEmitter<void>();
  @Output() openDatasets = new EventEmitter<void>();
  @Output() search = new EventEmitter<CanvasSearchMode>();
  @Output() drawShape = new EventEmitter<DrawShapeMode>();
  @Output() addPoint = new EventEmitter<void>();
  @Output() measure = new EventEmitter<void>();
  @Output() swipe = new EventEmitter<void>();
  /** Bubbled from ImportToolComponent — parsed GeoJSON features ready to register. */
  @Output() featuresImported = new EventEmitter<ImportedFeaturesPayload>();
  @Output() basemapChange = new EventEmitter<BasemapType>();
}
